import React from 'react';
import { Link } from 'react-router-dom';
import { Package, FileCheck, Plane, Ship, Download, Upload, TrendingUp, ArrowRight } from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: <Package className="w-10 h-10" />,
      title: 'Freight Forwarding',
      description: 'End-to-end coordination of cargo movement between South Africa, Europe, and Africa with complete visibility and carrier management.',
      link: '/services/freight-forwarding',
      image: 'https://images.unsplash.com/photo-1758900348926-cd1ccbc15fef?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: <FileCheck className="w-10 h-10" />,
      title: 'Customs Clearance',
      description: 'Expert clearing agent services ensuring compliance, documentation accuracy, and smooth customs processing for imports and exports.',
      link: '/services/customs-clearance',
      image: 'https://images.pexels.com/photos/7519247/pexels-photo-7519247.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940'
    },
    {
      icon: <Plane className="w-10 h-10" />,
      title: 'Air Freight',
      description: 'Fast, reliable air cargo services for time-sensitive shipments between Europe and Africa with priority handling.',
      link: '/services/air-freight',
      image: 'https://images.unsplash.com/photo-1761236099813-6a8189675c49?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: <Ship className="w-10 h-10" />,
      title: 'Ocean Freight',
      description: 'Cost-effective sea freight solutions for LCL and FCL shipments across major Europe–Africa trade routes.',
      link: '/services/ocean-freight',
      image: 'https://images.unsplash.com/photo-1768677859674-42c517273db8?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: <Download className="w-10 h-10" />,
      title: 'Import Solutions',
      description: 'Complete import coordination including freight booking, customs clearance, and delivery to your South African destination.',
      link: '/services/import-solutions',
      image: 'https://images.unsplash.com/photo-1761195696590-3490ea770aa1?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: <Upload className="w-10 h-10" />,
      title: 'Export Solutions',
      description: 'End-to-end export support from South Africa to Europe and Africa, handling documentation, freight, and customs.',
      link: '/services/export-solutions',
      image: 'https://images.unsplash.com/photo-1769997768068-4dc032f4c509?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85'
    },
    {
      icon: <TrendingUp className="w-10 h-10" />,
      title: 'Supply Chain Coordination',
      description: 'Broader logistics coordination supporting your supply chain operations across multiple shipments and routes.',
      link: '/services/supply-chain',
      image: 'https://images.unsplash.com/photo-1761195696590-3490ea770aa1?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85'
    }
  ];

  return (
    <div>
      {/* Hero */}
      <section className="py-24 md:py-32 bg-slate-50" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl">
            <span className="text-xs font-mono tracking-widest uppercase text-orange-500">Our Services</span>
            <h1 
              className="text-5xl md:text-7xl font-bold tracking-tight uppercase mt-4 mb-6"
              style={{fontFamily: 'Barlow Condensed, sans-serif'}}
            >
              Freight Forwarding & Logistics Solutions
            </h1>
            <p className="text-xl leading-relaxed text-slate-600">
              Complete cargo movement support for South African importers and exporters. From air and ocean freight to customs clearance and logistics coordination.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Link
                key={index}
                to={service.link}
                data-testid={`service-${index}`}
                className="group bg-white border border-slate-200 rounded-sm overflow-hidden hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300 hover:-translate-y-1"
              >
                <div className="h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-6">
                  <div className="text-orange-500 mb-3">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-slate-900">{service.title}</h3>
                  <p className="text-base leading-relaxed text-slate-600 mb-4">{service.description}</p>
                  <div className="flex items-center text-orange-500 font-medium text-sm">
                    Learn More <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 
            className="text-3xl md:text-5xl font-bold tracking-tight uppercase mb-6"
            style={{fontFamily: 'Barlow Condensed, sans-serif'}}
          >
            Need a Custom Solution?
          </h2>
          <p className="text-lg leading-relaxed text-slate-300 mb-8">
            Every business has unique logistics needs. Get in touch for a tailored freight and clearance solution.
          </p>
          <Link
            to="/contact"
            data-testid="services-cta-btn"
            className="bg-orange-500 text-white px-8 py-4 rounded-sm text-base font-medium hover:bg-orange-600 transition-colors duration-300 inline-flex items-center gap-2"
          >
            Request a Quote
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;